/**
 * D7A - consolidated B2B invoices, one row per client per cycle.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FileText } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useColors } from '../../hooks/useColors'
import EmptyState from '../../components/shared/EmptyState'
import {
  Card,
  Chip,
  Field,
  Modal,
  PageHeader,
  PillButton,
  SelectInput,
  StatCard,
  StatRow,
  TableHead,
  Td,
  TextInput,
} from '../../features/b2b/components/primitives'
import { formatDate, formatMoney } from '../../features/b2b/format'
import { b2bErrorMessage } from '../../features/b2b/errorCopy'
import LoadErrorState from '../../components/common/LoadErrorState'
import { withServerTimeout } from '../../components/common/loadErrorUtils'
import { b2bApi } from '../../services/b2bApi'
import type { B2bInvoiceListItem, B2bInvoiceRecord } from '../../services/b2bApi'

export default function B2bInvoicesPage() {
  const colors = useColors()
  const navigate = useNavigate()

  const [invoices, setInvoices] = useState<B2bInvoiceListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [clientFilter, setClientFilter] = useState('')
  const [status, setStatus] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  /** The invoice whose payment is being recorded, if any. */
  const [paying, setPaying] = useState<B2bInvoiceListItem | null>(null)
  const [payStatus, setPayStatus] = useState<B2bInvoiceRecord['payment_status']>('paid')
  const [payAmount, setPayAmount] = useState('')
  const [saving, setSaving] = useState(false)
  const [payError, setPayError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await withServerTimeout(b2bApi.listInvoices({ limit: 200 }))
      setInvoices(data.invoices)
    } catch (err) {
      setError(b2bErrorMessage(err, 'loading invoices'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const openPayment = useCallback((invoice: B2bInvoiceListItem) => {
    setPaying(invoice)
    setPayStatus(invoice.payment_status)
    // Pre-filled with what has already been recorded, so correcting a part
    // payment does not mean retyping it.
    setPayAmount(invoice.amount_paid > 0 ? String(invoice.amount_paid) : '')
    setPayError('')
  }, [])

  const savePayment = useCallback(async () => {
    if (!paying) return
    setSaving(true)
    setPayError('')
    try {
      const result = await b2bApi.updateInvoicePayment(paying.id, {
        payment_status: payStatus,
        // Omitted for Paid and Unpaid: the server fills in the total or zero,
        // so the two can never disagree.
        ...(payStatus === 'partly_paid' ? { amount_paid: payAmount } : {}),
      })
      setInvoices((current) =>
        current.map((invoice) =>
          invoice.id === result.invoice.id
            ? { ...invoice, ...result.invoice, outstanding: result.outstanding }
            : invoice,
        ),
      )
      setPaying(null)
    } catch (err) {
      setPayError(b2bErrorMessage(err, 'recording the payment'))
    } finally {
      setSaving(false)
    }
  }, [paying, payAmount, payStatus])

  /** The client list for the filter comes from the invoices themselves - no
   *  second request just to populate a dropdown. */
  const clients = useMemo(() => {
    const byId = new Map<string, string>()
    invoices.forEach((invoice) => byId.set(invoice.client_id, invoice.client_name))
    return [...byId.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [invoices])

  const filtered = useMemo(
    () =>
      invoices.filter((invoice) => {
        const generatedDay = String(invoice.generated_at).slice(0, 10)
        const matchesClient = !clientFilter || invoice.client_id === clientFilter
        const matchesStatus = !status || invoice.payment_status === status
        const matchesFrom = !from || generatedDay >= from
        const matchesTo = !to || generatedDay <= to
        return matchesClient && matchesStatus && matchesFrom && matchesTo
      }),
    [clientFilter, from, invoices, status, to],
  )

  const thisMonth = new Date().toISOString().slice(0, 7)
  const invoicedThisMonth = invoices
    .filter((invoice) => String(invoice.generated_at).slice(0, 7) === thisMonth)
    .reduce((sum, invoice) => sum + invoice.total_amount, 0)
  const outstanding = invoices.reduce((sum, invoice) => sum + invoice.outstanding, 0)
  const average =
    invoices.length > 0
      ? invoices.reduce((sum, invoice) => sum + invoice.total_amount, 0) / invoices.length
      : 0

  if (error) {
    return (
      <div>
        <PageHeader title="B2B Invoices" subtitle="Consolidated bills by client and cycle." />
        <LoadErrorState message={error} onRetry={() => void load()} />
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="B2B Invoices" subtitle="Consolidated bills by client and cycle." />

      <StatRow>
        <StatCard label="Invoiced This Month" value={formatMoney(invoicedThisMonth)} />
        <StatCard label="Outstanding" value={formatMoney(outstanding)} hint="Across all invoices" />
        <StatCard label="Average Invoice Value" value={formatMoney(average)} />
      </StatRow>

      <Card section>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
          <Field label="Client">
            <SelectInput
              value={clientFilter}
              aria-label="Client"
              onChange={(event) => setClientFilter(event.target.value)}
              style={{ minWidth: '180px' }}
            >
              <option value="">All clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="From">
            <TextInput
              type="date"
              value={from}
              aria-label="From date"
              onChange={(event) => setFrom(event.target.value)}
              style={{ width: '160px' }}
            />
          </Field>
          <Field label="To">
            <TextInput
              type="date"
              value={to}
              aria-label="To date"
              onChange={(event) => setTo(event.target.value)}
              style={{ width: '160px' }}
            />
          </Field>
          <Field label="Status">
            <SelectInput
              value={status}
              aria-label="Status"
              onChange={(event) => setStatus(event.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="">All</option>
              <option value="unpaid">Unpaid</option>
              <option value="partly_paid">Partly paid</option>
              <option value="paid">Paid</option>
            </SelectInput>
          </Field>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <TableHead
              columns={[
                { key: 'number', label: 'Invoice #' },
                { key: 'client', label: 'Client' },
                { key: 'cycle', label: 'Cycle' },
                { key: 'rooms', label: 'Rooms', align: 'right' },
                { key: 'total', label: 'Total', align: 'right' },
                { key: 'generated', label: 'Generated On' },
                { key: 'status', label: 'Status' },
                { key: 'actions', label: '' },
              ]}
            />
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8}>
                    <div style={{ padding: '16px', display: 'grid', gap: '8px' }}>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <div
                          key={index}
                          className="rapidry-pulse"
                          style={{ height: '28px', borderRadius: '8px', background: colors.surface }}
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState
                      icon={FileText}
                      title="No invoices match those filters"
                      description="Generate one from a client's open cycle."
                    />
                  </td>
                </tr>
              ) : (
                filtered.map((invoice) => {
                  return (
                    <tr key={invoice.id}>
                      <Td strong style={{ whiteSpace: 'nowrap' }}>
                        {invoice.invoice_number}
                      </Td>
                      <Td>{invoice.client_name}</Td>
                      <Td muted>{invoice.cycle_label ?? '-'}</Td>
                      <Td align="right">{invoice.rooms_billed}</Td>
                      <Td align="right" strong>
                        {formatMoney(invoice.total_amount)}
                      </Td>
                      <Td muted>{formatDate(invoice.generated_at)}</Td>
                      <Td>
                        <Chip tone={invoice.payment_status === 'paid' ? 'success' : invoice.payment_status === 'partly_paid' ? 'warning' : 'info'}>
                          {invoice.payment_status === 'partly_paid' ? 'Partly paid' : invoice.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                        </Chip>
                      </Td>
                      <Td align="right" style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className="font-body"
                            onClick={() => navigate(`/b2b/invoices/${invoice.id}`)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: colors.textPrimary,
                              fontWeight: 600,
                              fontSize: '13px',
                              cursor: 'pointer',
                            }}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="font-body"
                            onClick={() => navigate(`/b2b/invoices/${invoice.id}`)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: colors.textMuted,
                              fontWeight: 600,
                              fontSize: '13px',
                              cursor: 'pointer',
                            }}
                          >
                            PDF
                          </button>
                          <button
                            type="button"
                            className="font-body"
                            onClick={() => openPayment(invoice)}
                            aria-label={`Record payment for ${invoice.invoice_number}`}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: colors.textPrimary,
                              fontWeight: 600,
                              fontSize: '13px',
                              cursor: 'pointer',
                            }}
                          >
                            Payment
                          </button>
                          <button
                            type="button"
                            className="font-body"
                            disabled
                            title="Wired in build phase B4"
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: colors.textMuted,
                              fontWeight: 600,
                              fontSize: '13px',
                              cursor: 'not-allowed',
                              opacity: 0.5,
                            }}
                          >
                            WhatsApp
                          </button>
                        </div>
                      </Td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recording a payment. Small on purpose (FRD §8): a status and an amount,
          no gateway and no reconciliation. The server reconciles the two, so the
          amount field only appears where it can mean something. */}
      {paying ? (
        <Modal
          title={`Payment · ${paying.invoice_number}`}
          onClose={() => setPaying(null)}
          width="440px"
          footer={
            <>
              <PillButton variant="secondary" onClick={() => setPaying(null)} disabled={saving}>
                Cancel
              </PillButton>
              <PillButton onClick={() => void savePayment()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </PillButton>
            </>
          }
        >
          <div style={{ display: 'grid', gap: '14px' }}>
            <div
              className="font-body"
              style={{ fontSize: '13px', color: colors.textSecondary }}
            >
              {paying.client_name} · {formatMoney(paying.total_amount)} billed
            </div>

            <Field label="Status">
              <SelectInput
                value={payStatus}
                aria-label="Payment status"
                onChange={(event) => {
                  setPayStatus(event.target.value as B2bInvoiceRecord['payment_status'])
                  setPayError('')
                }}
              >
                <option value="unpaid">Unpaid</option>
                <option value="partly_paid">Partly paid</option>
                <option value="paid">Paid</option>
              </SelectInput>
            </Field>

            {payStatus === 'partly_paid' ? (
              <Field label="Amount received">
                <TextInput
                  value={payAmount}
                  aria-label="Amount received"
                  inputMode="decimal"
                  placeholder="0.00"
                  onChange={(event) => {
                    setPayAmount(event.target.value)
                    setPayError('')
                  }}
                />
              </Field>
            ) : (
              <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                {payStatus === 'paid'
                  ? `Recording the full ${formatMoney(paying.total_amount)} as received.`
                  : 'Nothing recorded against this invoice.'}
              </p>
            )}

            {payError ? (
              <p role="alert" className="font-body" style={{ margin: 0, fontSize: '13px', color: 'var(--chip-danger-ink)' }}>
                {payError}
              </p>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
